// =============================================================================
// io/sync/saveCheckInPnl.ts — the self-reported P&L on the Session view.
// -----------------------------------------------------------------------------
// THREE COLUMNS, and only ONE KEY inside the blob it writes.
//
// ── 🚨 WHY THIS IS NOT AN ARGUMENT ON `saveSessionContract` ──
//
// That writer exists to say the opposite thing. Its header: the contract blob
// holds four timestamps written DURING the session — when a daily stop was hit,
// when the trader acknowledged it — and `dllAcknowledgedAt` is compared against
// every later trade to flag it as an override of the trader's own stop. So the
// pre-market form writes five keys and is forbidden the rest, `manualPnl`
// included.
//
// The check-in is the other side of that fence: it writes `manualPnl` and is
// forbidden the five. Merging the two writers would hand the planning form the
// power to overwrite a figure typed an hour into the session.
//
// ⚠ THE MERGE IS OVER THE ROW'S OWN STORED BLOB, never a rebuild — same hazard,
// same rule. `{}` is correct only for a date with no row yet.
//
// ⚠ CLEARING DELETES THE KEY. `checkInToBlob` explains why an explicit null is
// not the same thing, and why `0` is a real override that must survive.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { checkInToBlob } from '../../domain/journal';
import { reportFailureAs } from '../diagnostics/errorLog';

/** Every column this module writes. Fixed, and asserted by a test. */
export const CHECKIN_COLUMNS = ['user_id', 'date', 'session_contract'] as const;

export interface SaveCheckInArgs {
  sb: SupabaseClient;
  userId: string;
  date: string;
  /** The self-reported figure, or null to fall back to the imported trades. */
  manualPnl: number | null;
  /** The row's stored contract blob. `{}` only for a date that has no row yet. */
  rawContract: Readonly<Record<string, unknown>>;
  baselineUpdatedAt: string | null;
}

export interface SaveCheckInResult {
  updatedAt: string | null;
  /** The blob just written — it becomes what the NEXT save merges over. */
  rawContract: Record<string, unknown>;
  error: string | null;
  heldStale: boolean;
}

export async function saveCheckInPnl(args: SaveCheckInArgs): Promise<SaveCheckInResult> {
  const { sb, userId, date, manualPnl, rawContract, baselineUpdatedAt } = args;
  const blob = checkInToBlob(manualPnl, rawContract);

  // ⚠ FAIL-OPEN, like every other journal writer.
  if (baselineUpdatedAt) {
    const { data, error } = await sb
      .from('journal_entries')
      .select('date, updated_at')
      .eq('user_id', userId)
      .eq('date', date);

    if (!error && Array.isArray(data) && data.length === 1) {
      const cloud = (data[0] as unknown as Record<string, unknown>)['updated_at'];
      if (typeof cloud === 'string'
        && new Date(cloud).getTime() > new Date(baselineUpdatedAt).getTime()) {
        return {
          updatedAt: null,
          rawContract: { ...rawContract },
          heldStale: true,
          error:
            `This day was changed somewhere else after you loaded it — probably the `
            + `other client or another tab. Nothing was saved, so neither version is `
            + `lost. Reload to see the newer copy, then re-enter the figure.`,
        };
      }
    }
  }

  const { data, error } = await sb
    .from('journal_entries')
    .upsert({ user_id: userId, date, session_contract: blob }, { onConflict: 'user_id,date' })
    .select('date, updated_at');

  if (error) {
    return {
      updatedAt: null, rawContract: { ...rawContract }, heldStale: false,
      error: reportFailureAs('save check-in P&L', error, `Save failed: ${error.message}`),
    };
  }
  if (!Array.isArray(data) || data.length !== 1) {
    return {
      updatedAt: null, rawContract: { ...rawContract }, heldStale: false,
      error:
        `The save returned ${Array.isArray(data) ? data.length : 'no'} rows instead of one, `
        + `so it cannot be confirmed. Reload before editing again.`,
    };
  }

  const ts = (data[0] as unknown as Record<string, unknown>)['updated_at'];
  return {
    updatedAt: typeof ts === 'string' ? ts : null,
    rawContract: blob,
    heldStale: false,
    error: null,
  };
}
